import { useAppDispatch, useAppSelector } from "@/app/appStore";
import { InfoItem } from "@/components/info-item";
import {
  addJourneyTransaction,
  CustomerAction,
  decreaseSelectedProductCount,
  increaseSelectedProductCount,
  selectAnnotationData,
  selectIsEditable,
  selectIsSkuSectionPlanogramVisible,
  selectSelectedGondola,
  selectSelectedProduct,
  setAnnotationData,
  setCurrentCamera,
  setSelectedPolygon,
  setSelectedProduct,
  setSelectedSkuSectionId,
  setPlanogramViewVisibility,
  toggleSkuSectionPlanogram,
  setSelectedEventById,
  selectActiveJourneyTransactions,
  setGondolaByName,
  setGondolaDropdownValueByName,
  setSelectedCameraMode,
  setPlanogramOverlayVisibility,
  setHighlightedTimelineEventId,
  setSelectedProductCount,
} from "@/entities/annotation";
import {
  Text,
  Flex,
  IconButton,
  SegmentedControl,
  TextField,
  Button,
  Callout,
} from "@radix-ui/themes";
import {
  InfoCircledIcon,
  TableIcon,
  TargetIcon,
  TrashIcon,
} from "@radix-ui/react-icons";
import ProductItem from "../../ui/product-item";
import { hasIdenticalAnnotationTimestamp } from "../../lib/helpers";
import { useState } from "react";
import Counter from "../../ui/counter";
import {
  ANNOTATION_TYPES,
  CAMERA_MODES,
  CUSTOMER_ACTIONS,
} from "@/entities/annotation/model/consts";
import toast from "react-hot-toast";
import { displayUUID, formatAbsoluteCoordinates } from "@/shared";
import { useTrackedHotkeys } from "@/shared/hooks/useTrackedHotkeys";
import { useCameras } from "../../hooks/useCameras";
import { AnnotationFormWrapper } from "../../ui/annotation-form-wrapper";
import { useTimelineData } from "../../hooks/useTimelineData";
import { useCalibration } from "../../lib/useCalibration";
import { usePlanogram } from "../../lib/usePlanogram";
import { usePlayer } from "@tezzro/player";

const TransactionAnnotationForm = () => {
  const annotationData = useAppSelector(selectAnnotationData);
  const dispatch = useAppDispatch();
  const { addTimelineTransactionEvent } = useTimelineData();
  const selectedProduct = useAppSelector(selectSelectedProduct);
  const selectedGondola = useAppSelector(selectSelectedGondola);
  const isPlanogramViewVisible = useAppSelector(
    selectIsSkuSectionPlanogramVisible
  );
  const activeJourneyTransactions = useAppSelector(
    selectActiveJourneyTransactions
  );
  const isEditable = useAppSelector(selectIsEditable);
  const player = usePlayer();
  const [note, setNote] = useState("");
  const [action, setAction] = useState<CustomerAction>(CUSTOMER_ACTIONS.TAKE);
  const cameraData = useCameras();
  const { planogram } = usePlanogram();
  const { calibration } = useCalibration();

  const handleRemoveProduct = () => {
    dispatch(setSelectedSkuSectionId(null));
    dispatch(setSelectedPolygon(null));
    dispatch(setSelectedProduct(null));
  };

  const changeGondolaAndCamera = (cameraId: string, timestamp: string) => {
    const cameraGondolaName = cameraData?.findCameraGondolaName?.(cameraId);

    player.seek(timestamp, cameraId);
    dispatch(setGondolaByName(cameraGondolaName));
    dispatch(setGondolaDropdownValueByName(cameraGondolaName));
    dispatch(setCurrentCamera(cameraId));
    dispatch(setSelectedCameraMode(CAMERA_MODES.ALL));
  };

  const handleApply = () => {
    if (
      !annotationData?.gondolaId ||
      !annotationData?.gondolaName ||
      !annotationData?.timestamp ||
      !annotationData?.pinCoordinates ||
      !selectedProduct ||
      !planogram?.id ||
      !calibration?.id
    ) {
      return null;
    }

    if (
      hasIdenticalAnnotationTimestamp(
        activeJourneyTransactions,
        annotationData.timestamp
      )
    ) {
      toast.error(
        "Same type annotations within a journey must have different timestamps"
      );
      return;
    }

    const customId = crypto.randomUUID();

    dispatch(
      addJourneyTransaction({
        id: customId,
        orderId: annotationData.orderId,
        journeyId: annotationData.journeyId,
        timestamp: annotationData?.timestamp,
        barcode: selectedProduct?.barcode,
        imageUrl: selectedProduct?.imageUrl,
        name: selectedProduct?.name,
        skuId: selectedProduct?.skuId,
        variantId: selectedProduct?.variantId,
        count: selectedProduct?.count,
        /**
         * We are sending internal UoM for the transaction annotation because:
         * The internal UoM (uom_internal) is in millis (g, ml) for all products, regardless of their external UoM. It's used for internal calculations and ensures consistency across the system.
         * The external UoM (uom) is the usually in kilos (kg, litre) and for display purposes only.
         */
        uom: selectedProduct?.unitOfMeasurementInternal,
        action: action,
        gondolaId: annotationData?.gondolaId,
        gondolaName: annotationData?.gondolaName,
        planogramId: planogram.id,
        cameraImageWidth: annotationData.cameraImageWidth,
        cameraImageHeight: annotationData.cameraImageHeight,
        pinCoordinates: annotationData.pinCoordinates,
        cameraId: parseInt(annotationData.cameraId),
        calibrationId: calibration.id,
        notes: note,
      })
    );

    dispatch(setSelectedEventById(customId));
    dispatch(setHighlightedTimelineEventId(customId));
    addTimelineTransactionEvent({
      id: customId,
      type: ANNOTATION_TYPES.TRANSACTION,
      journeyId: annotationData.journeyId,
      timestamp: annotationData?.timestamp,
    });

    changeGondolaAndCamera(annotationData.cameraId, annotationData.timestamp);
    clearAnnotationData();
  };

  useTrackedHotkeys("esc", (e) => {
    e.preventDefault();
    handleCancel();
  });

  useTrackedHotkeys("t", (e) => {
    e.preventDefault();
    setAction(CUSTOMER_ACTIONS.TAKE);
  });

  useTrackedHotkeys("p", (e) => {
    e.preventDefault();
    setAction(CUSTOMER_ACTIONS.PUT);
  });

  useTrackedHotkeys("comma", (e) => {
    e.preventDefault();
    dispatch(decreaseSelectedProductCount());
  });

  useTrackedHotkeys(".", (e) => {
    e.preventDefault();
    dispatch(increaseSelectedProductCount());
  });

  useTrackedHotkeys("mod + enter", (e) => {
    e.preventDefault();
    handleApply();
  });

  if (!annotationData) {
    return null;
  }

  const handleCancel = () => {
    changeGondolaAndCamera(annotationData.cameraId, annotationData.timestamp);
    clearAnnotationData();
  };

  const clearAnnotationData = () => {
    dispatch(setPlanogramOverlayVisibility(false));
    dispatch(setPlanogramViewVisibility(false));
    dispatch(setSelectedSkuSectionId(null));
    dispatch(setSelectedPolygon(null));
    dispatch(setSelectedProduct(null));
    dispatch(setAnnotationData(null));
  };

  const jumpToAnnotationTimePoint = () => {
    const gondolaName = cameraData?.findCameraGondolaName?.(
      annotationData.cameraId
    );

    player.seek(annotationData?.timestamp, annotationData.cameraId);

    dispatch(setPlanogramViewVisibility(false));
    dispatch(setGondolaDropdownValueByName(gondolaName || null));
    dispatch(setGondolaByName(gondolaName || null));
    dispatch(setCurrentCamera(annotationData.cameraId));
    cameraData.setCameraModeFromCameraId(annotationData.cameraId);
  };

  const handleSkuPlanogramToggle = () => {
    if (!selectedGondola) {
      return toast.error("Please select a gondola camera");
    }

    if (!isPlanogramViewVisible) {
      const shelfTrackingCameraId =
        selectedGondola.shelfTrackingCameras?.[0]?.id;

      player.changeCamera(shelfTrackingCameraId);
      dispatch(setCurrentCamera(shelfTrackingCameraId));
      dispatch(setSelectedCameraMode(CAMERA_MODES.SHELF));
    } else {
      changeGondolaAndCamera(annotationData.cameraId, annotationData.timestamp);
    }

    dispatch(toggleSkuSectionPlanogram());
  };

  const pinCoordinates = formatAbsoluteCoordinates(
    annotationData?.pinCoordinates.relativeX,
    annotationData?.pinCoordinates.relativeY,
    annotationData.cameraImageWidth,
    annotationData.cameraImageHeight
  );

  return (
    <AnnotationFormWrapper>
      <InfoItem label="Event type" value="Journey Transaction Annotation" />
      <InfoItem
        label="Journey ID"
        copyValue={annotationData?.journeyId}
        value={displayUUID(annotationData?.journeyId)}
      />
      <InfoItem
        label="Order ID"
        value={displayUUID(annotationData.orderId)}
        copyValue={annotationData.orderId}
      />
      <InfoItem label="Timestamp" value={annotationData?.timestamp}>
        <IconButton variant="ghost" onClick={jumpToAnnotationTimePoint}>
          <TargetIcon />
        </IconButton>
      </InfoItem>

      <InfoItem label="Pin coordinates (x,y)" value={pinCoordinates} />
      <InfoItem
        label="Camera image dimensions"
        value={`${annotationData?.cameraImageWidth}x${annotationData?.cameraImageHeight}`}
      />
      <InfoItem label="Annotated camera" value={annotationData?.cameraId} />
      <InfoItem label="Calibration ID" value={annotationData?.calibrationId} />
      <InfoItem label="Gondola name" value={annotationData?.gondolaName} />
      <InfoItem label="Gondola ID" value={annotationData?.gondolaId} />
      <InfoItem label="Planogram ID" value={annotationData?.planogramId} />

      {isEditable ? (
        <>
          <Text size="1" color="gray" className="min-w-fit">
            Note:
          </Text>
          <TextField.Root
            size="1"
            mx="1"
            placeholder="Some kind of note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </>
      ) : (
        <InfoItem label="Note" value={note} />
      )}

      <Text size="2" weight="bold" mt="3" as="p">
        Item:
      </Text>

      <Flex align="center" justify="between" my="4">
        <Button
          size="2"
          variant={isPlanogramViewVisible ? "solid" : "soft"}
          color="gray"
          mr="1"
          onClick={handleSkuPlanogramToggle}
        >
          <TableIcon />
          SKU Planogram View
        </Button>
      </Flex>

      {selectedProduct ? (
        <>
          <ProductItem
            name={selectedProduct?.name}
            imageUrl={selectedProduct?.imageUrl}
            barcode={selectedProduct?.barcode}
            skuId={selectedProduct?.skuId}
            unitOfMeasurement={selectedProduct?.unitOfMeasurementInternal}
          />
          <Flex>
            <SegmentedControl.Root
              value={action}
              onValueChange={(value: CustomerAction) => setAction(value)}
              variant="surface"
              size="2"
              className="flex-1"
            >
              <SegmentedControl.Item value={CUSTOMER_ACTIONS.TAKE}>
                Take (t)
              </SegmentedControl.Item>
              <SegmentedControl.Item value={CUSTOMER_ACTIONS.PUT}>
                Put (p)
              </SegmentedControl.Item>
            </SegmentedControl.Root>
          </Flex>

          <Flex align="center" justify="between" mt="3">
            <Flex align="center">
              <Text color="gray" mr="1">
                Quantity:
              </Text>
              <Counter
                value={selectedProduct?.count}
                onDecrement={() => dispatch(decreaseSelectedProductCount())}
                onIncrement={() => dispatch(increaseSelectedProductCount())}
                onValueChange={(value) => {
                  dispatch(setSelectedProductCount(value));
                }}
              />
              <Text color="gray" highContrast ml="1">
                ({selectedProduct.unitOfMeasurementInternal || "UoM"})
              </Text>
            </Flex>
            <IconButton
              variant="soft"
              color="red"
              onClick={handleRemoveProduct}
            >
              <TrashIcon color="red" />
            </IconButton>
          </Flex>
        </>
      ) : (
        <Callout.Root color="gray" className="items-center">
          <Callout.Icon>
            <InfoCircledIcon />
          </Callout.Icon>
          <Callout.Text>
            A single SKU must be chosen from the planogram.
          </Callout.Text>
        </Callout.Root>
      )}

      {isEditable ? (
        <Flex justify="between" mt="8">
          <Button
            size="1"
            color="gray"
            variant="soft"
            type="button"
            onClick={handleCancel}
          >
            Cancel (esc)
          </Button>
          <Button
            size="1"
            color="gray"
            highContrast
            type="submit"
            disabled={!selectedProduct}
            onClick={handleApply}
          >
            Apply (ctrl + enter)
          </Button>
        </Flex>
      ) : null}
    </AnnotationFormWrapper>
  );
};

export default TransactionAnnotationForm;
